import { createStyles } from '@mantine/core';
import { useAuthUser } from '../../api';
import { CHECKBOX_AFFECTS_TABLE_ROW_CLASS_NAMES } from './constants';

export const ROW_HEIGHT = 44;
export const HEADER_HEIGHT = 35;
export const FOOTER_HEIGHT = 40;

export const BREADCRUMB_HEIGHT = 52;
export const BOTTOM_PADDING = 24;
export const TAB_HEIGHT = 47;

interface ITableStylesProps {
	hideCheckbox?: boolean;
	stickyColumn?: number;
	hasNestingFilter?: boolean;
	noBorder?: boolean;
	withStickyColumnBorder?: boolean;
	disableSelectedDisplay?: boolean;
	withHover: boolean;
}

export const useTableStyles = createStyles(
	(
		theme,
		{
			stickyColumn = 1,
			hideCheckbox = false,
			hasNestingFilter = false,
			noBorder = false,
			withStickyColumnBorder,
			disableSelectedDisplay = false,
			withHover,
		}: ITableStylesProps
	) => {
		const { isViewerOrGuestUser } = useAuthUser();

		const stickyColumnIdentifier = `tr td:nth-of-type(${stickyColumn})`;
		const stickyHdrIdentifier = `tr th:nth-of-type(${stickyColumn})`;

		return {
			table: {},
			root: {
				// Hide hover states for viewer and guest users:
				'td .mantine-UnstyledButton-root': {
					'&:hover': {
						background: isViewerOrGuestUser ? 'none' : undefined,
					},
					'div[aria-label="placeholder"]': {
						display: isViewerOrGuestUser ? 'none' : undefined,
					},
				},
				// Hide row action buttons by default
				'*[aria-label="row-actions"]': {
					visibility: 'hidden',
				},
				// Hide placeholder by default
				'div[aria-label="placeholder"]': {
					display: 'none',
				},
				tr: {
					height: ROW_HEIGHT,
					'&:has(button[data-expanded="true"])': {
						td: {
							background: theme.other.getColor('surface/primary/hover'),
						},
						'div[aria-label="placeholder"]': {
							display: 'block',
						},
						'*[aria-label="row-actions"]': {
							visibility: 'visible',
						},
					},
					':hover': {
						td: {
							background: withHover
								? theme.other.getColor('surface/primary/hover')
								: undefined,
						},
						'div[aria-label="placeholder"]': {
							display: 'block !important',
						},
						'td button span.mantine-Button-label': {
							visibility: 'visible',
						},
						'td button div.mantine-Text-root': {
							visibility: 'visible',
						},
						'*[aria-label="row-actions"]': {
							visibility: 'visible',
						},
					},
				},
				// Ensure the last row in the table has a bottom border
				'tbody tr:last-of-type td': {
					borderBottom: `1px solid ${theme.other.getColor('border/secondary/default')}`,
				},
				[`tr:has(.${CHECKBOX_AFFECTS_TABLE_ROW_CLASS_NAMES!.input}:checked)`]: {
					td: disableSelectedDisplay
						? {}
						: {
								background: `${theme.other.getColor('surface/primary/selected')} !important`,
							},
				},
				// Selectively hide checkbox:
				'.mantine-Checkbox-root': {
					visibility: hideCheckbox ? 'hidden' : 'visible',
				},
				td: {
					// So badges and other React content don't overlap.
					overflow: 'hidden',
					// Let us define padding on the React component level.
					paddingTop: '0 !important',
					paddingBottom: '0 !important',
					borderBottom: noBorder ? 'none !important' : undefined,
					background: theme.other.getColor('surface/primary/default'),
				},
				[`${stickyColumnIdentifier}, ${stickyHdrIdentifier}`]: {
					borderRight: `1px solid ${theme.other.getColor('border/secondary/default')}`,
					borderRightWidth: withStickyColumnBorder ? '1px' : '0',
					position: 'sticky !important' as 'sticky',
					left:
						Math.max(stickyColumn - 1, 0) * theme.other.space[8] +
						// `+ theme.other.space[2]` is to account for the first column
						// left-padding, only on those with checkboxes, otherwise it's 0.
						(stickyColumn > 1 ? theme.other.space[2] : 0),
					zIndex: 1,
				},
				borderRadius: noBorder ? 0 : theme.radius.md,
				borderColor: theme.other.getColor('border/secondary/default'),
				'input[type="checkbox"]': {
					borderColor: theme.other.getColor('border/input/default'),
				},
				'input[type="checkbox"]:disabled': {
					opacity: 0,
					cursor: 'not-allowed',
					pointerEvents: 'none',
				},
				'div.mantine-Pagination-dots': {
					display: 'none',
				},
				'button.mantine-Pagination-control': {
					display: 'none',
					border: 'none',
				},
				'button.mantine-Pagination-control:has(svg)': {
					display: 'flex',
				},
				'thead > tr > th': {
					lineHeight: theme.spacing.md,
					backgroundColor: theme.other.getColor('surface/secondary/default'),
					'&:hover': {
						backgroundColor: `${theme.other.getColor('surface/secondary/hover')} !important`,
					},
					'&:has(div.mantine-Menu-dropdown)': {
						backgroundColor: theme.other.getColor('surface/secondary/selected'),
					},
				},
			},
			header: {},
			pagination: {
				div: {
					fontSize: theme.fontSizes.xs,
				},
				height: theme.other.space[10],
				color: theme.other.getColor('text/secondary/default'),
				backgroundColor: theme.other.getColor('surface/secondary/default'),
				fontWeight: '500 !important' as unknown as number,
				button: {
					backgroundColor: theme.other.getColor('surface/secondary/default'),
				},
				'button[not-disabled]:hover': {
					backgroundColor: theme.other.getColor('surface/secondary/hover'),
				},
				'button[disabled]': {
					backgroundColor: theme.other.getColor('surface/secondary/default'),
				},
				'button[aria-haspopup="menu"]': {
					display: 'none',
					visibility: 'hidden',
				},
			},
		};
	}
);
